import { createFileRoute, Link } from "@tanstack/react-router";
import {
	buildLocalizedSeoLinks,
	getLocalizedSeoUrls,
	ogImageUrl,
} from "@/lib/seo";

const slug = "ai-agent-batch-image-generation";
const path = `/blog/${slug}`;

export const Route = createFileRoute(path)({
	component: Article,
	head: ({ matches }) => {
		const seoUrls = getLocalizedSeoUrls({ matches, internalPath: path });
		return {
			meta: [
				{
					title:
						"Batch Image Generation for AI Agents: Patterns and Pitfalls - AgentFramer",
				},
				{
					name: "description",
					content:
						"Five hundred product shots overnight. Concurrency, idempotency, retries, and the cost rules that keep batches predictable.",
				},
				{
					property: "og:title",
					content: "Batch Image Generation for AI Agents",
				},
				{
					property: "og:description",
					content:
						"Patterns and pitfalls for batch image generation in AI agent workflows.",
				},
				{ property: "og:image", content: ogImageUrl(`blog-${slug}`) },
				{ property: "og:url", content: seoUrls.canonicalUrl },
				{ property: "og:type", content: "article" },
				{ name: "twitter:card", content: "summary_large_image" },
				{ name: "twitter:image", content: ogImageUrl(`blog-${slug}`) },
			],
			links: buildLocalizedSeoLinks({ matches, internalPath: path }),
		};
	},
});

function Article() {
	return (
		<article className="space-y-10">
			<div className="space-y-2">
				<p className="text-xs text-muted-foreground/60">April 2026</p>
				<h1 className="text-2xl font-medium tracking-tight">
					Batch image generation for AI agents: patterns and pitfalls
				</h1>
				<p className="text-sm text-muted-foreground">
					Five hundred ecommerce product shots, due before the marketing team
					logs in tomorrow. The agent has eight hours and one job. Here is the
					shape of work that finishes on time without burning the budget.
				</p>
			</div>

			<div className="space-y-4 text-sm leading-relaxed text-muted-foreground">
				<h2 className="text-base font-medium text-foreground pt-2">
					The 500-shot overnight scenario
				</h2>
				<p>
					Concrete setup. A catalog team has 500 SKUs. Each SKU needs one hero
					shot on FLUX 1.1 pro and three lifestyle variants on SDXL Lightning.
					The agent kicks off at 22:00, results checked at 07:00. That is 2,000
					generations in nine hours. At a naive one-at-a-time pace, even a fast
					model finishes a fraction of the work. The job only fits inside the
					window if the agent treats the batch as a set, not a loop.
				</p>
				<p>
					The patterns below are the ones that actually keep that run on the
					rails: bounded concurrency, deterministic idempotency keys,
					retry-with-fallback, a circuit breaker, and budget rules the agent can
					enforce without a human in the loop.
				</p>
				<p>
					None of this is theoretical. Most of the failures you will hit on a
					two-thousand-image run do not show up at twenty. The interesting thing
					about scale is which problems become statistically certain. At twenty
					images, a 1% NSFW false positive rate means you probably see zero. At
					two thousand, you will see twenty, and they will arrive in clusters
					because the same kind of prompt template trips the same filter rule.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					Concurrency, not iteration
				</h2>
				<p>
					Naive agents iterate one job at a time. The right pattern fires jobs
					concurrently and bounds the parallelism so a slow tail does not block
					the runway. AgentFramer's per-workspace concurrency limit is set high
					enough that batches of fifty in flight at once are routine; the
					agent's job is to feed that pipe without overshooting it.
				</p>
				<p>
					A semaphore-bounded <code>Promise.allSettled</code> is the right
					primitive. It runs up to N jobs concurrently, never aborts the whole
					batch on a single failure, and lets you classify outcomes afterward.
					The 500-shot run becomes ten waves of fifty rather than five hundred
					sequential calls.
				</p>
				<p>
					The wrong primitive is <code>Promise.all</code>. One rejection unwinds
					the whole batch, you lose track of which jobs succeeded, and the agent
					ends up retrying work that already completed. Use{" "}
					<code>allSettled</code> and let the caller decide what counts as a
					failure.
				</p>

				<pre className="rounded-md bg-card p-4 text-xs text-foreground overflow-x-auto">{`// Bounded concurrency over a batch with deterministic idempotency keys.
import { createHash } from "node:crypto";

type Job = {
  sku: string;
  variant: "hero" | "lifestyle-1" | "lifestyle-2" | "lifestyle-3";
  model: "flux-1.1-pro" | "sdxl-lightning";
  prompt: string;
  promptVersion: string;
};

const idempotencyKey = (j: Job) =>
  createHash("sha256")
    .update([j.sku, j.variant, j.model, j.promptVersion].join("|"))
    .digest("hex");

async function semaphore<T>(limit: number, tasks: (() => Promise<T>)[]) {
  const results: PromiseSettledResult<T>[] = new Array(tasks.length);
  let cursor = 0;
  const workers = Array.from({ length: limit }, async () => {
    while (cursor < tasks.length) {
      const i = cursor++;
      try {
        results[i] = { status: "fulfilled", value: await tasks[i]() };
      } catch (err) {
        results[i] = { status: "rejected", reason: err };
      }
    }
  });
  await Promise.all(workers);
  return results;
}

export async function runBatch(jobs: Job[], generate: (j: Job, key: string) => Promise<unknown>) {
  const tasks = jobs.map((j) => () => generate(j, idempotencyKey(j)));
  return semaphore(50, tasks);
}`}</pre>

				<h2 className="text-base font-medium text-foreground pt-2">
					Idempotency: deterministic keys per logical image
				</h2>
				<p>
					Agents retry. Networks blip. Workers crash. Without idempotency, every
					retry is a new charge for work you already paid for. AgentFramer's{" "}
					<code>generate_image</code> accepts an <code>idempotency_key</code>.
					The recipe that holds up under load is a hash of every input that
					defines the logical image:
				</p>
				<pre className="rounded-md bg-card p-4 text-xs text-foreground overflow-x-auto">{`sha256(sku + "|" + variant + "|" + model + "|" + prompt_version)`}</pre>
				<p>
					Same inputs, same key, same result. A retried call returns the
					existing job; a re-run after a prompt edit (new{" "}
					<code>prompt_version</code>) generates fresh images. This is what lets
					the agent treat <code>generate_image</code> as safe to call twice. See
					the{" "}
					<Link
						to="/help/mcp-tools"
						className="text-primary hover:text-primary/80 transition-colors"
					>
						MCP tool reference
					</Link>{" "}
					for the full parameter list.
				</p>
				<p>
					A subtle but important rule: keep the seed out of the key unless you
					intend the seed to be part of the image's identity. If the seed is
					randomized per call, every retry computes a new key and you have lost
					idempotency. Pin the seed (or omit it from the key) and you keep the
					safety net. The same goes for any per-call jitter in prompts — bake
					stable phrasing into <code>prompt_version</code> rather than mutating
					the prompt string between attempts.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					Retry-with-fallback, not retry-the-same-thing
				</h2>
				<p>
					In a batch of two thousand, a few percent will fail. The failures
					cluster into shapes you can plan for:
				</p>
				<ul className="list-disc pl-5 space-y-1">
					<li>
						<strong className="text-foreground">NSFW filter.</strong> Soften the
						prompt (strip ambiguous adjectives, add neutral context) and retry
						once. If it still fires, mark and skip.
					</li>
					<li>
						<strong className="text-foreground">Per-model rate limit.</strong>{" "}
						Do not retry the same model. Fall back to an equivalent: FLUX 1.1
						pro to FLUX dev for hero, SDXL Lightning to FLUX schnell for
						variants.
					</li>
					<li>
						<strong className="text-foreground">429 / transient.</strong>{" "}
						Exponential backoff with jitter. Cap at three attempts.
					</li>
					<li>
						<strong className="text-foreground">Opaque upstream error.</strong>{" "}
						One retry, then mark for human review.
					</li>
				</ul>
				<p>
					The agent should keep a small state map (<code>jobId → status</code>)
					and re-shoot the failed subset at the end of the run, not abort the
					batch. Persist that map outside the agent's working memory — a
					database row, a JSON file in object storage, anywhere a restart can
					rehydrate it. Combined with idempotency keys, this means a crashed
					agent can resume without re-billing a single completed job.
				</p>
				<p>
					Distinguish between fallback at the job level and fallback at the
					model level. Job-level fallback (soften the prompt, try again) is
					cheap and the right first move for content-policy failures.
					Model-level fallback (FLUX 1.1 pro to FLUX dev) is what saves the
					batch when an entire upstream is degraded. Wire both, and order them
					so the cheaper option runs first.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					Circuit breaker
				</h2>
				<p>
					Every batch eventually meets a degraded provider. Without a brake, the
					agent burns retries on jobs that were never going to succeed. Rule: if
					more than 10% of jobs in the last sixty seconds fail, pause for sixty
					seconds before resuming. Two consecutive trips and the agent halts the
					run and reports state.
				</p>
				<p>
					The thresholds are tunable, but the shape matters more than the
					numbers. A cooldown that is too short turns the breaker into a noise
					generator; one that is too long stalls a recoverable batch. Sixty
					seconds is long enough for a transient upstream blip to clear, short
					enough that the overnight window is not blown by one trip.
				</p>

				<pre className="rounded-md bg-card p-4 text-xs text-foreground overflow-x-auto">{`class CircuitBreaker {
  private events: { t: number; ok: boolean }[] = [];
  private trippedUntil = 0;
  private trips = 0;

  constructor(
    private windowMs = 60_000,
    private threshold = 0.1,
    private cooldownMs = 60_000,
    private maxTrips = 2,
  ) {}

  record(ok: boolean) {
    const now = Date.now();
    this.events.push({ t: now, ok });
    this.events = this.events.filter((e) => now - e.t <= this.windowMs);
    const total = this.events.length;
    if (total < 20) return;
    const failRate = this.events.filter((e) => !e.ok).length / total;
    if (failRate > this.threshold) {
      this.trippedUntil = now + this.cooldownMs;
      this.trips++;
    }
  }

  async gate() {
    if (this.trips >= this.maxTrips) throw new Error("circuit open: halt batch");
    const wait = this.trippedUntil - Date.now();
    if (wait > 0) await new Promise((r) => setTimeout(r, wait));
  }
}`}</pre>

				<h2 className="text-base font-medium text-foreground pt-2">
					Cost discipline
				</h2>
				<p>
					Batches are where cost surprises happen. The same prompt accidentally
					re-issued at fifty-wide concurrency is a five-figure mistake before
					the agent notices. Four rules that keep it boring:
				</p>
				<ol className="list-decimal pl-5 space-y-1">
					<li>
						Read <code>get_credits</code> before the batch. If the projected
						spend (jobs × model price) exceeds available credits, stop and ask.
					</li>
					<li>
						Hard per-batch cap. The agent maintains a running cost estimate and
						aborts the wave if it crosses the cap, even mid-run.
					</li>
					<li>
						Draft-then-final. SDXL Lightning passes for first-look variants;
						FLUX 1.1 pro only on the hero shot or after human approval. For this
						catalog run, that ratio alone cuts spend by roughly 60% versus
						running the premium model on every variant.
					</li>
					<li>
						Read <code>get_credits</code> again after the batch and reconcile
						against the projection. A delta over 5% means a bug, not a pricing
						surprise.
					</li>
				</ol>
				<p>
					Model selection is a cost lever as much as a quality one — the{" "}
					<Link
						to="/models"
						className="text-primary hover:text-primary/80 transition-colors"
					>
						models page
					</Link>{" "}
					lists the FLUX, SDXL, Ideogram, Imagen, and Playground options with
					their relative pricing.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					Storage at scale: don't carry 500 URLs in context
				</h2>
				<p>
					Fifty signed URLs in an agent's context window is fine. Two thousand
					is not — the context bill alone makes the batch unprofitable, and the
					agent starts confusing one URL for another. The pattern: store only
					job IDs during the run, then call <code>list_recent_generations</code>{" "}
					once the batch is complete to materialize the URLs into the downstream
					system (catalog DB, asset pipeline, CDN). Signed URLs default to
					30-day retention, which is more than enough headroom for an overnight
					job and a morning review.
				</p>
				<p>
					A useful discipline: the agent's chat history should never contain
					more than a handful of URLs at once. If it does, the batch is
					mis-shaped. Push results to storage, push pointers to a queue, and
					keep the conversation focused on decisions, not artifacts.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					When batch is the wrong unit
				</h2>
				<p>
					Batch is the right shape when the work is repetitive, the prompt
					template is settled, and the success criterion is "ship 2,000 images
					that look like the spec." Batch is the wrong shape when:
				</p>
				<ul className="list-disc pl-5 space-y-1">
					<li>
						<strong className="text-foreground">
							Heavy art-direction iteration.
						</strong>{" "}
						The hero illustration for a product launch is a one-of-one. You need
						ten attempts, each informed by the last. That is a conversation, not
						a queue.
					</li>
					<li>
						<strong className="text-foreground">Brand reviews.</strong> Every
						frame needs a human eye before the next one is generated. A batch of
						fifty just produces fifty things to throw away.
					</li>
					<li>
						<strong className="text-foreground">Creative exploration.</strong>{" "}
						When the goal is "find a direction we like," variance is the
						feature. Generating two thousand variations of the same prompt is
						the opposite of what you want; you want twenty very different
						prompts.
					</li>
				</ul>
				<p>
					For those, drop concurrency to one and put the human in the loop.
					Batch infrastructure should be able to do this without code changes —
					the same <code>generate_image</code> tool, same idempotency keys, just
					a smaller wave size and a pause between each call.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					What "done" looks like at 07:00
				</h2>
				<p>
					A successful overnight run leaves three things on the morning review
					desk: a count (1,994 of 2,000 succeeded), a small list of failures
					with reasons (six NSFW false positives, prompts attached), and a
					credit reconciliation that matches the projection within a few
					percent. No URL dump in chat. No "the batch crashed at item 370 and I
					do not know what state it is in." That is the difference between batch
					generation that scales and batch generation that gets paged on.
				</p>
				<p>
					The whole pattern in one line: bound concurrency, key every job, retry
					with fallbacks, brake on systemic failure, cap the spend, and pull
					URLs out of context. More worked examples in the{" "}
					<Link
						to="/help/tool-call-patterns"
						className="text-primary hover:text-primary/80 transition-colors"
					>
						tool-call patterns guide
					</Link>
					.
				</p>
			</div>
		</article>
	);
}
